import * as Sentry from '@sentry/react';
import { Event, EventHint } from '@sentry/react';
import { AxiosError, isAxiosError } from 'axios';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

function sentryBeforeSend(event: Sentry.ErrorEvent, hint: EventHint) {
  if (isAnonymousError(event)) {
    return null;
  }

  addAxiosContextRecursive(event, hint.originalException);

  return event;
}

function isAnonymousError(event: Event) {
  try {
    if (event?.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename === '<anonymous>') {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function addAxiosContextRecursive(event: Event, error: unknown) {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
}

function addAxiosContext(event: Event, error: AxiosError) {
  if (error.response) {
    const contexts = { ...event.contexts };
    contexts.Axios = { Response: error.response };
    event.contexts = contexts;
  }
}

const ANONYMOUS_ID_KEY = 'sentry_anonymous_id';

function getOrCreateAnonymousId(): string {
  try {
    const existingId = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (existingId) {
      return existingId;
    }
    const newId = `anon_${crypto.randomUUID()}`;
    localStorage.setItem(ANONYMOUS_ID_KEY, newId);
    return newId;
  } catch {
    // localStorage not available, generate ephemeral ID
    return `anon_${crypto.randomUUID()}`;
  }
}

export function initSentry() {
  Sentry.init({
    dsn: 'https://01208fa84da2b095459c911f8cf9e90f@o4507072624984064.ingest.de.sentry.io/4510114356658256',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.contextLinesIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    beforeSend: sentryBeforeSend,
    tracesSampleRate: process.env.ENV === 'prod' ? 0.1 : 1.0,
    tracePropagationTargets: ['https://api.glite.ai/', 'https://api.test.glite.ai/'],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    ignoreErrors: [
      'Failed to fetch',
      'Load failed',
      't is not a function',
      'undefined is not an object',
      'Request aborted',
      'Java object is gone',
      "Failed to read the 'localStorage' property",
      "Failed to read a named property 'removeEventListener' from 'Window'",
    ],
    environment: process.env.ENV,
  });

  Sentry.setUser({ id: getOrCreateAnonymousId() });
}
